import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { echo } from '@/shared/echo'
import { gameKey } from './useGame'

/** Кусок API pusher-js, который нам нужен: следить за состоянием сокета. */
type Connection = {
  state?: string
  bind: (event: string, handler: () => void) => void
  unbind: (event: string, handler: () => void) => void
}

function connection(): Connection | null {
  // Через unknown и с проверками: коннектор появляется не сразу, а в тестах
  // Echo вообще подменён заглушкой — падать из-за этого экран не должен.
  const connector = echo.connector as unknown as
    | { pusher?: { connection?: Connection } }
    | undefined

  return connector?.pusher?.connection ?? null
}

/**
 * Живо ли соединение с Reverb — и досинхронизация после обрыва.
 *
 * Пока сокет лежал, партия могла уйти вперёд на несколько ходов: события
 * доставляются только живым подписчикам, истории у канала нет. Поэтому при
 * восстановлении связи состояние перезапрашивается, а не ждёт следующего
 * чужого хода.
 */
export function useConnection(gameId: string): boolean {
  const client = useQueryClient()
  const [online, setOnline] = useState(() => connection()?.state !== 'disconnected')

  useEffect(() => {
    const socket = connection()

    if (socket === null) return

    const onConnected = () => {
      setOnline(true)
      void client.invalidateQueries({ queryKey: gameKey(gameId) })
    }
    const onLost = () => setOnline(false)

    socket.bind('connected', onConnected)
    socket.bind('disconnected', onLost)
    socket.bind('unavailable', onLost)

    return () => {
      socket.unbind('connected', onConnected)
      socket.unbind('disconnected', onLost)
      socket.unbind('unavailable', onLost)
    }
  }, [gameId, client])

  return online
}

/**
 * Связь вернулась — досинхронизируйтесь.
 *
 * Отдельным хуком, потому что то же самое нужно чату: у канала нет истории,
 * и разговор, ушедший вперёд, пока сокет лежал, задним числом не приедет
 * (features/chat/useChat.ts). Своего доступа к сокету у чата быть не должно
 * — иначе про состояние соединения знали бы два места.
 *
 * Обработчик берётся через ref: он пересобирается на каждом рендере, а
 * переподписываться на сокет из-за этого незачем.
 */
export function useReconnect(handler: () => void): void {
  const latest = useRef(handler)
  latest.current = handler

  useEffect(() => {
    const socket = connection()

    if (socket === null) return

    const onConnected = () => latest.current()

    socket.bind('connected', onConnected)

    return () => {
      socket.unbind('connected', onConnected)
    }
  }, [])
}
